import api from './api';
import type { Invoice, InvoiceExtractionStatus, InvoiceExtractedData, InvoiceStatus, PaginatedResponse } from '../types/venue';

const base = (venueId: string) => `/admin/venue/${venueId}/invoices`;

const invoiceService = {
  getInvoices: async (
    venueId: string,
    page = 1,
    pageSize = 20,
    status?: InvoiceStatus,
    supplierId?: string,
  ): Promise<PaginatedResponse<Invoice>> => {
    const params: Record<string, unknown> = { page, pageSize };
    if (status) params.status = status;
    if (supplierId) params.supplierId = supplierId;
    const response = await api.get(base(venueId), { params });
    return response.data;
  },

  getInvoicesBySupplier: async (
    supplierId: string,
    page = 1,
    pageSize = 50,
  ): Promise<PaginatedResponse<Invoice>> => {
    const response = await api.get('/admin/invoices', { params: { supplierId, page, pageSize } });
    return response.data;
  },

  createInvoice: async (
    venueId: string,
    data: {
      supplierId: string;
      invoiceNumber: string;
      purchaseOrderId?: string;
      invoiceDate?: string;
      dueDate?: string;
      totalAmount?: number;
      notes?: string;
    },
  ): Promise<Invoice> => {
    const response = await api.post(base(venueId), data);
    return response.data;
  },

  updateInvoice: async (
    venueId: string,
    invoiceId: string,
    data: {
      invoiceNumber?: string;
      supplierId?: string;
      invoiceDate?: string | null;
      dueDate?: string | null;
      totalAmount?: number | null;
      currencyCode?: string | null;
      notes?: string;
      status?: InvoiceStatus;
    },
  ): Promise<Invoice> => {
    const response = await api.patch(`${base(venueId)}/${invoiceId}`, data);
    return response.data;
  },

  uploadAttachment: async (
    venueId: string,
    invoiceId: string,
    file: File,
  ): Promise<Invoice> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(
      `${base(venueId)}/${invoiceId}/attachments/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  deleteAttachment: async (
    venueId: string,
    invoiceId: string,
    storageKey: string,
  ): Promise<Invoice> => {
    const response = await api.delete(`${base(venueId)}/${invoiceId}/attachments`, {
      data: { storageKey },
    });
    return response.data;
  },

  getAttachmentUrl: (venueId: string, invoiceId: string, attachmentId: string): string => {
    return `${base(venueId)}/${invoiceId}/attachments/${attachmentId}/file`;
  },

  extractInvoice: async (
    venueId: string,
    invoiceId: string,
  ): Promise<{
    extractionStatus: InvoiceExtractionStatus;
    extractedData: InvoiceExtractedData | null;
    extractionError?: string | null;
  }> => {
    const response = await api.post(`${base(venueId)}/${invoiceId}/extract`);
    return response.data;
  },
};

export default invoiceService;
